import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { EnhancePatientNotePromptType } from '@ffd/api-client';
import { ContextMenuHeaderButton, GenerateTextAction } from '@libs/ui-lib';
import { SvgIconComponent, SvgIcons } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { SpeechRecognitionStatus } from '@libs/speech-recognition';

@Component({
  selector: 'lib-enhance-buttons',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, TranslateModule, LottieComponent],
  templateUrl: './enhance-buttons.component.html',
  styleUrl: './enhance-buttons.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnhanceButtonsComponent {
  public readonly contextMenuHeaderConfig = input.required<ContextMenuHeaderButton[]>();
  public readonly isGenerating = input<boolean>(false);
  public readonly transcriptionStatus = input<SpeechRecognitionStatus>('idle');
  public readonly enhanceLottieOptions = input<AnimationOptions>({
    path: 'assets/animations/enhance.json',
  });

  public readonly buttonClick = output<EnhancePatientNotePromptType>();

  protected readonly GenerateTextAction = GenerateTextAction;
  protected readonly currentEnhanceType = signal<GenerateTextAction | null>(null);
  protected readonly isCurrentEnhanceTypeRewrite = computed(() => {
    return (
      this.currentEnhanceType() === GenerateTextAction.NoteRewrite ||
      this.currentEnhanceType() === GenerateTextAction.CollabRewrite ||
      this.currentEnhanceType() === GenerateTextAction.HealthHistoryRewrite
    );
  });

  public getIconKey(type: GenerateTextAction): SvgIcons {
    switch (type) {
      case GenerateTextAction.CollabBullets:
      case GenerateTextAction.HealthHistoryBullets: {
        return 'px-20-bullet-list';
      }

      case GenerateTextAction.CollabConcise:
      case GenerateTextAction.NoteConcise:
      case GenerateTextAction.HealthHistoryConcise: {
        return 'px-16-concise';
      }

      case GenerateTextAction.CollabRewrite:
      case GenerateTextAction.HealthHistoryRewrite:
      case GenerateTextAction.NoteRewrite: {
        return 'px-16-enhance-2';
      }

      case GenerateTextAction.NoteSoap: {
        return 'px-16-soap';
      }
    }
  }

  public mapToEnhancePromptType(action: GenerateTextAction): EnhancePatientNotePromptType {
    switch (action) {
      case GenerateTextAction.HealthHistoryBullets: {
        return EnhancePatientNotePromptType.BULLETS;
      }
      case GenerateTextAction.HealthHistoryConcise: {
        return EnhancePatientNotePromptType.CONCISE;
      }
      case GenerateTextAction.HealthHistoryRewrite: {
        return EnhancePatientNotePromptType.REWRITE;
      }
      default: {
        return EnhancePatientNotePromptType.REWRITE;
      }
    }
  }

  public onButtonClick(type: GenerateTextAction): void {
    this.currentEnhanceType.set(type);
    const enhanceType = this.mapToEnhancePromptType(type);
    this.buttonClick.emit(enhanceType);
  }
}
